import {
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import TestStatusCheck from 'components/rbac-settings/teams/TestStatusCheck'
import { URLHelperText } from 'components/services/create/URLHelperText'
import {
  buildTeamExternalIntegration,
  StatusCheckFormValues,
  StatusCheckMonitorSelectionRadioValue
} from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useTranslation } from 'hooks/use-translation'

export const AWSMonitorURL: React.FC<{
  disabled: boolean
  radioValue: StatusCheckMonitorSelectionRadioValue
}> = ({ disabled, radioValue }) => {
  const { trans } = useTranslation()
  const { control, formState, getValues } =
    useFormContext<StatusCheckFormValues>()

  const [externalIntegration, evaluationConfiguration] =
    useWatch<StatusCheckFormValues>({
      control,
      name: ['externalIntegration', 'evaluationConfiguration']
    })

  const {
    handleSuccess,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  return (
    <FormControl component={Stack} sx={{ rowGap: 1 }}>
      <Typography>
        {trans('forms.save_status_check.connect_monitor.monitor_url.prompt')}
      </Typography>
      <Controller
        control={control}
        name="monitorUrl"
        render={({ field }) => (
          <TextField
            {...field}
            aria-describedby="monitor-url-helper-text"
            data-testid="status-check-monitor-url-input"
            disabled={radioValue !== StatusCheckMonitorSelectionRadioValue.URL}
            error={Boolean(formState.errors.monitorUrl)}
            helperText={formState.errors.monitorUrl?.message}
            label={trans(
              'forms.save_status_check.connect_monitor.monitor_url.text_field.label'
            )}
            variant="outlined"
            onChange={(e) => {
              // Need to reset validation states on monitor URL change
              // TODO: reset results displays for test components
              if (isStatusCheckConnectionValidated) {
                setIsStatusCheckConnectionValidated(false)
              }
              if (isStatusCheckEvaluationValidated) {
                setIsStatusCheckEvaluationValidated(false)
              }

              field.onChange(e)
            }}
          />
        )}
      />
      <FormHelperText id="monitor-url-helper-text">
        <URLHelperText integration={externalIntegration} />
      </FormHelperText>

      <TestStatusCheck
        disabled={disabled || isTestStatusCheckDisabled}
        evaluationConfiguration={evaluationConfiguration}
        integration={buildTeamExternalIntegration(getValues())}
        onSuccess={handleSuccess}
      />
    </FormControl>
  )
}
